import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UploadIcon from '@mui/icons-material/Upload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  deleteFile,
  fileServeUrl,
  getFileStats,
  listFiles,
  updateFile,
  uploadFile,
  type FileRecord,
} from '../services/filesApi';
import FileThumbnail from '../components/FileThumbnail';
import { ContentPageLayout } from '../components/Shared';

const FILE_TYPES = ['all', 'image', 'audio', 'video', 'score'];
const FILE_CATEGORIES = ['profile', 'content', 'thumbnail', 'background', 'other'] as const;
const NO_CHANGE = '';

type BulkTagsMode = 'none' | 'replace' | 'append';

const formatSize = (bytes?: number) => {
  if (!bytes) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
};

const parseTags = (input: string) =>
  input
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

const FilesAdminPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiles, setTotalFiles] = useState(0);
  const [fileType, setFileType] = useState('all');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState<{ totalFiles?: number; totalSize?: number } | null>(null);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkCategory, setBulkCategory] = useState(NO_CHANGE);
  const [bulkTagsMode, setBulkTagsMode] = useState<BulkTagsMode>('none');
  const [bulkTags, setBulkTags] = useState('');

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allOnPageSelected = files.length > 0 && files.every((f) => selectedSet.has(f._id));
  const someOnPageSelected = files.some((f) => selectedSet.has(f._id));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        page,
        limit: 25,
        sortBy: 'uploadedAt',
        sortOrder: 'desc',
      };
      if (fileType !== 'all') params.fileType = fileType;
      if (search.trim()) params.search = search.trim();

      const [listRes, statsRes] = await Promise.all([
        listFiles(params),
        getFileStats().catch(() => null),
      ]);
      setFiles(listRes.data?.files || []);
      setTotalPages(listRes.data?.pagination?.totalPages || 1);
      setTotalFiles(listRes.data?.pagination?.totalFiles || 0);
      if (statsRes?.data) setStats(statsRes.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [page, fileType, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => files.some((f) => f._id === id)));
  }, [files]);

  const toggleSelectAllOnPage = () => {
    if (allOnPageSelected) {
      const pageIds = new Set(files.map((f) => f._id));
      setSelectedIds((prev) => prev.filter((id) => !pageIds.has(id)));
    } else {
      const merged = new Set([...selectedIds, ...files.map((f) => f._id)]);
      setSelectedIds([...merged]);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const clearSelection = () => setSelectedIds([]);

  const openBulkEdit = () => {
    setBulkCategory(NO_CHANGE);
    setBulkTagsMode('none');
    setBulkTags('');
    setBulkEditOpen(true);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMsg(null);
    try {
      await uploadFile(file);
      setMsg(`Uploaded ${file.name}`);
      await load();
    } catch (err: any) {
      setMsg(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRename = async () => {
    if (!renameId || !renameValue.trim()) return;
    try {
      await updateFile(renameId, { originalName: renameValue.trim() });
      setRenameId(null);
      setMsg('File renamed.');
      await load();
    } catch (err: any) {
      setMsg(err?.message || 'Rename failed');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await deleteFile(id);
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      setMsg('File deleted.');
      await load();
    } catch (err: any) {
      setMsg(err?.message || 'Delete failed');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected file(s)?`)) return;
    setBulkSaving(true);
    try {
      let deleted = 0;
      for (const id of selectedIds) {
        await deleteFile(id);
        deleted += 1;
      }
      setSelectedIds([]);
      setMsg(`Deleted ${deleted} file(s).`);
      await load();
    } catch (err: any) {
      setMsg(err?.message || 'Bulk delete failed');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkEditApply = async () => {
    const hasCategory = bulkCategory !== NO_CHANGE;
    const hasTags = bulkTagsMode !== 'none' && bulkTags.trim().length > 0;
    if (!hasCategory && !hasTags) {
      setMsg('Choose at least one field to update.');
      return;
    }
    if (selectedIds.length === 0) return;

    setBulkSaving(true);
    try {
      let updated = 0;
      for (const id of selectedIds) {
        const file = files.find((f) => f._id === id);
        const body: Record<string, unknown> = {};
        if (hasCategory) body.category = bulkCategory;
        if (hasTags) {
          if (bulkTagsMode === 'replace') {
            body.tags = parseTags(bulkTags);
          } else {
            const existing = file?.tags || [];
            body.tags = [...new Set([...existing, ...parseTags(bulkTags)])];
          }
        }
        await updateFile(id, body);
        updated += 1;
      }
      setBulkEditOpen(false);
      setSelectedIds([]);
      setMsg(`Updated ${updated} file(s).`);
      await load();
    } catch (err: any) {
      setMsg(err?.message || 'Bulk edit failed');
    } finally {
      setBulkSaving(false);
    }
  };

  const uploaderLabel = (f: FileRecord) => {
    const u = f.uploadedBy;
    if (u && typeof u === 'object') return u.nickname || u.email || '—';
    return '—';
  };

  return (
    <ContentPageLayout>
    <Box>
      <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
        File management
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Upload, search, rename, and delete platform files.
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {msg ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert> : null}

      {stats ? (
        <Box display="flex" gap={2} mb={2}>
          <Paper variant="outlined" sx={{ p: 1.5, flex: 1, textAlign: 'center' }}>
            <Typography variant="h6">{stats.totalFiles ?? totalFiles}</Typography>
            <Typography variant="caption" color="text.secondary">Total files</Typography>
          </Paper>
        </Box>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            select
            size="small"
            label="Type"
            value={fileType}
            onChange={(e) => { setPage(1); setFileType(e.target.value); }}
            sx={{ minWidth: 120 }}
          >
            {FILE_TYPES.map((t) => (
              <MenuItem key={t} value={t}>{t === 'all' ? 'All types' : t}</MenuItem>
            ))}
          </TextField>
          <Button component="label" variant="contained" startIcon={<UploadIcon />} disabled={uploading}>
            {uploading ? 'Uploading…' : 'Upload'}
            <input type="file" hidden onChange={handleUpload} />
          </Button>
        </Box>
      </Paper>

      {selectedIds.length > 0 ? (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mr: 1 }}>
            {selectedIds.length} selected
          </Typography>
          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={openBulkEdit} disabled={bulkSaving}>
            Bulk edit
          </Button>
          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleBulkDelete} disabled={bulkSaving}>
            Delete
          </Button>
          <Button size="small" onClick={clearSelection} disabled={bulkSaving}>
            Clear
          </Button>
        </Paper>
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 48 }}>
                  <Checkbox
                    size="small"
                    checked={allOnPageSelected}
                    indeterminate={someOnPageSelected && !allOnPageSelected}
                    onChange={toggleSelectAllOnPage}
                    inputProps={{ 'aria-label': 'Select all files on this page' }}
                  />
                </TableCell>
                <TableCell sx={{ color: 'primary.main', width: 64 }} />
                <TableCell sx={{ color: 'primary.main' }}>Name</TableCell>
                <TableCell sx={{ color: 'primary.main' }}>Type</TableCell>
                <TableCell sx={{ color: 'primary.main' }}>Category</TableCell>
                <TableCell sx={{ color: 'primary.main' }}>Tags</TableCell>
                <TableCell sx={{ color: 'primary.main' }}>Size</TableCell>
                <TableCell sx={{ color: 'primary.main' }}>Uploader</TableCell>
                <TableCell sx={{ color: 'primary.main' }}>Uploaded</TableCell>
                <TableCell align="center" sx={{ color: 'primary.main' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((f) => (
                <TableRow key={f._id} selected={selectedSet.has(f._id)} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedSet.has(f._id)}
                      onChange={() => toggleSelect(f._id)}
                      inputProps={{ 'aria-label': `Select ${f.originalName || f.filename}` }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1, width: 64 }}>
                    <FileThumbnail file={f} size={48} />
                  </TableCell>
                  <TableCell>{f.originalName || f.filename || '—'}</TableCell>
                  <TableCell>{f.fileType || '—'}</TableCell>
                  <TableCell>{f.category || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 160 }}>
                    <Typography variant="body2" noWrap title={(f.tags || []).join(', ')}>
                      {(f.tags || []).length ? (f.tags || []).join(', ') : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatSize(f.fileSize)}</TableCell>
                  <TableCell>{uploaderLabel(f)}</TableCell>
                  <TableCell>{f.uploadedAt ? new Date(f.uploadedAt).toLocaleString() : '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" href={fileServeUrl(f._id)} target="_blank" rel="noopener noreferrer">
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => { setRenameId(f._id); setRenameValue(f.originalName || f.filename || ''); }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(f._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box display="flex" justifyContent="center" gap={1} mt={2}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Typography variant="body2" sx={{ alignSelf: 'center' }}>Page {page} / {totalPages}</Typography>
            <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </Box>
        </>
      )}

      {renameId ? (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Rename file</Typography>
          <Box display="flex" gap={1}>
            <TextField size="small" fullWidth value={renameValue} onChange={(e) => setRenameValue(e.target.value)} />
            <Button variant="contained" onClick={handleRename}>Save</Button>
            <Button onClick={() => setRenameId(null)}>Cancel</Button>
          </Box>
        </Paper>
      ) : null}

      <Dialog open={bulkEditOpen} onClose={() => !bulkSaving && setBulkEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk edit {selectedIds.length} file(s)</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={bulkCategory}
                onChange={(e) => setBulkCategory(e.target.value)}
              >
                <MenuItem value={NO_CHANGE}>— No change —</MenuItem>
                {FILE_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Tags</InputLabel>
              <Select
                label="Tags"
                value={bulkTagsMode}
                onChange={(e) => setBulkTagsMode(e.target.value as BulkTagsMode)}
              >
                <MenuItem value="none">— No change —</MenuItem>
                <MenuItem value="replace">Replace all tags</MenuItem>
                <MenuItem value="append">Add tags</MenuItem>
              </Select>
            </FormControl>

            {bulkTagsMode !== 'none' ? (
              <TextField
                size="small"
                fullWidth
                label={bulkTagsMode === 'replace' ? 'New tags (comma-separated)' : 'Tags to add (comma-separated)'}
                value={bulkTags}
                onChange={(e) => setBulkTags(e.target.value)}
                placeholder="e.g. lesson, backing-track"
              />
            ) : null}

            <Typography variant="caption" color="text.secondary">
              Only fields you change will be applied to every selected file.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkEditOpen(false)} disabled={bulkSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkEditApply} disabled={bulkSaving}>
            {bulkSaving ? 'Applying…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ContentPageLayout>
  );
};

export default FilesAdminPage;
